import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublishIcon from '@mui/icons-material/Publish';
import HistoryIcon from '@mui/icons-material/History';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SoaTableRenderer from '../../protocol/sections/s9-assessment-schedule/components/SoaTableRenderer';
import catalog from '../../protocol/sections/s9-assessment-schedule/section9.catalog.json';
import s9AssessmentScheduleService from '../../services/s9AssessmentSchedule.service';
import clinicalIntakeService from '../../services/clinicalIntake.service';

const BOOTSTRAP_MESSAGE =
  'Start Section 1.3 collecting: build complete SOA structure (columns + rowGroups), grid empty.';

const PHASE_COLORS = {
  collecting: 'default',
  draft: 'info',
  ready_for_review: 'warning',
  finalized: 'success',
};

function buildDefaultWorkspace() {
  return {
    soa: { tables: [], flags: [] },
    expectedDecisions: (catalog.expectedDecisions || []).map((label) => ({
      label,
      value: '',
      status: 'pending',
    })),
    chatPhase: 'collecting',
    subsections: [],
  };
}

function decisionStatusColor(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'finalized' || s === 'confirmed') return 'success';
  if (s === 'provisional') return 'warning';
  return 'default';
}

const S9AssessmentScheduleForm = ({ projectId, initialData = {} }) => {
  const [messages, setMessages] = useState([]);
  const [workspace, setWorkspace] = useState(buildDefaultWorkspace);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contextJson, setContextJson] = useState({});
  const [contextLabel, setContextLabel] = useState('');
  const [versions, setVersions] = useState([]);
  const [publishedVersion, setPublishedVersion] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [dataPlatformPushing, setDataPlatformPushing] = useState(false);
  const chatScrollRef = useRef(null);

  const studyId = useMemo(
    () => initialData?.sponsor_protocol_identifier || initialData?.study_id || '',
    [initialData]
  );

  const loadWorkspace = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [wsRes, intakeRes] = await Promise.allSettled([
        s9AssessmentScheduleService.getS9Workspace({ projectId, studyId }),
        clinicalIntakeService.getLatestClinicalIntake(projectId),
      ]);

      if (wsRes.status === 'fulfilled' && wsRes.value) {
        const saved = wsRes.value.workspace || buildDefaultWorkspace();
        setWorkspace({
          soa: saved.soa || { tables: [], flags: [] },
          expectedDecisions: saved.expectedDecisions?.length
            ? saved.expectedDecisions
            : buildDefaultWorkspace().expectedDecisions,
          chatPhase: saved.chatPhase || 'collecting',
          subsections: saved.subsections || [],
          decisionScaffold: saved.decisionScaffold || {},
        });
        if (Array.isArray(wsRes.value.messages) && wsRes.value.messages.length) {
          setMessages(wsRes.value.messages);
        }
      }

      if (intakeRes.status === 'fulfilled' && intakeRes.value) {
        setContextJson(intakeRes.value);
        const si = intakeRes.value.study_identification || {};
        const proto =
          si.protocol_number ||
          intakeRes.value.sponsor_protocol_identifier ||
          '';
        const acronym = si.trial_acronym || si.study_acronym || '';
        setContextLabel(
          [proto, acronym].filter(Boolean).join(' · ') || 'Clinical intake loaded'
        );
      } else if (initialData && Object.keys(initialData).length) {
        setContextJson(initialData);
        setContextLabel('Using form data (intake fetch unavailable)');
      } else {
        setContextLabel('No clinical intake loaded for this project');
      }

      try {
        const latest = await s9AssessmentScheduleService.getLatestS9Soa({ projectId, studyId });
        if (latest?.success && latest.data?.published) {
          setPublishedVersion(latest.data.versionNumber);
        }
      } catch (_e) {
        setPublishedVersion(null);
      }
    } catch (e) {
      setError(e.message || 'Failed to load SOA workspace.');
    } finally {
      setLoading(false);
    }
  }, [projectId, studyId, initialData]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  const sendMessage = async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || busy || !projectId) return;

    setBusy(true);
    setError(null);
    try {
      // Refresh intake for this project right before each turn (avoid stale/wrong study blob).
      let freshContext = contextJson;
      try {
        const latest = await clinicalIntakeService.getLatestClinicalIntake(projectId);
        if (latest) {
          freshContext = latest;
          setContextJson(latest);
          const si = latest.study_identification || {};
          const proto =
            si.protocol_number || latest.sponsor_protocol_identifier || '';
          const acronym = si.trial_acronym || si.study_acronym || '';
          setContextLabel(
            [proto, acronym].filter(Boolean).join(' · ') || 'Clinical intake loaded'
          );
        }
      } catch (_e) {
        /* keep previous contextJson */
      }

      const userMsg = { id: `user-${Date.now()}`, role: 'user', rawContent: trimmed };
      const nextMessages = [...messages, userMsg];

      const res = await s9AssessmentScheduleService.postS9Chat({
        projectId,
        studyId,
        contextJson: freshContext,
        upstreamSections: {},
        messages: nextMessages,
        workspace,
      });

      const assistantMsg = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        rawContent: res.assistantReply || '',
      };

      setMessages([...nextMessages, assistantMsg]);
      setWorkspace({
        soa: res.soa || { tables: [], flags: [] },
        expectedDecisions: res.expectedDecisions || workspace.expectedDecisions,
        chatPhase: res.chatPhase || workspace.chatPhase,
        subsections: res.subsections || [],
        decisionScaffold: res.decisionScaffold || {},
      });
      setDraft('');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'SOA chat request failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleResumeEditing = async () => {
    if (!projectId) return;
    setBusy(true);
    try {
      await s9AssessmentScheduleService.postS9ResumeEditing({ projectId, studyId });
      setWorkspace((prev) => ({ ...prev, chatPhase: 'draft' }));
    } catch (e) {
      setError(e.message || 'Could not resume editing.');
    } finally {
      setBusy(false);
    }
  };

  const handleResetSoa = async () => {
    if (!projectId) return;
    const confirmed = window.confirm(
      'Reset SOA completely? This clears chat history, tables, decisions, and starts collecting from the beginning. Published versions are kept and can still be restored from Versions.'
    );
    if (!confirmed) return;

    setBusy(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await s9AssessmentScheduleService.postS9ResetWorkspace({
        projectId,
        studyId,
      });
      setMessages([]);
      setWorkspace(res.workspace || buildDefaultWorkspace());
      setDraft('');
      setSuccessMessage(res.message || 'SOA reset. You can start collecting again.');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not reset SOA.');
    } finally {
      setBusy(false);
    }
  };

  const tables = workspace?.soa?.tables || [];
  const chatPhase = workspace?.chatPhase || 'collecting';

  const handlePublish = async () => {
    if (!projectId || !tables.length) return;
    setBusy(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await s9AssessmentScheduleService.publishS9Version({
        projectId,
        studyId,
        changeNote: 'Published from SOA Architect chat',
        workspace,
      });
      setWorkspace((prev) => ({ ...prev, chatPhase: 'finalized' }));
      setPublishedVersion(res?.version?.versionNumber ?? null);
      setSuccessMessage(res?.message || `Published SOA version ${res?.version?.versionNumber}`);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Publish failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleSendToDataPlatform = async () => {
    if (!projectId) return;
    setDataPlatformPushing(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await s9AssessmentScheduleService.pushSoaToDataPlatform({ projectId, studyId });
      if (res?.success) {
        setSuccessMessage(res?.message || 'Sent the published SOA to the Data Platform.');
      } else {
        setError(res?.message || 'Failed to send SOA to the Data Platform.');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to send SOA to the Data Platform.');
    } finally {
      setDataPlatformPushing(false);
    }
  };

  const openVersions = async () => {
    if (!projectId) return;
    setBusy(true);
    try {
      const res = await s9AssessmentScheduleService.listS9Versions({ projectId, studyId });
      setVersions(res?.versions || []);
      setVersionsOpen(true);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load versions.');
    } finally {
      setBusy(false);
    }
  };

  const handleRollback = async (versionNumber) => {
    if (!projectId) return;
    setBusy(true);
    try {
      const res = await s9AssessmentScheduleService.rollbackS9Version({
        projectId,
        studyId,
        versionNumber,
      });
      setPublishedVersion(res?.version?.versionNumber ?? null);
      setVersionsOpen(false);
      setSuccessMessage(res?.message || `Rolled back to v${versionNumber}`);
      await loadWorkspace();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Rollback failed.');
    } finally {
      setBusy(false);
    }
  };

  const decisions = workspace.expectedDecisions || [];
  const decidedCount = decisions.filter((d) => {
    const s = String(d.status || '').toLowerCase();
    return s === 'finalized' || s === 'confirmed' || s === 'provisional' || Boolean(d.value);
  }).length;

  if (!projectId) {
    return (
      <Alert severity="info">
        Open a study with a project selected to use the Schedule of Activities studio.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: { xs: 'auto', md: 'calc(100vh - 220px)' },
        minHeight: { xs: 560, md: 0 },
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ flexShrink: 0 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            Section 1.3 — Schedule of Activities
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Chat to define structure, then place visit marks on the SOA tables.
          </Typography>
          {contextLabel ? (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`Intake context: ${contextLabel}`}
              sx={{ mt: 0.75 }}
            />
          ) : null}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={chatPhase.replace(/_/g, ' ')}
            color={PHASE_COLORS[chatPhase] || 'default'}
            sx={{ textTransform: 'capitalize' }}
          />
          {publishedVersion != null && (
            <Chip size="small" color="success" variant="outlined" label={`Published v${publishedVersion}`} />
          )}
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<RestartAltIcon />}
            onClick={handleResetSoa}
            disabled={busy}
          >
            Reset SOA
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={openVersions}
            disabled={busy}
          >
            Versions
          </Button>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<PublishIcon />}
            onClick={handlePublish}
            disabled={busy || !tables.length}
          >
            Publish to protocol
          </Button>
          {chatPhase === 'finalized' && (
            <Button size="small" startIcon={<RefreshIcon />} onClick={handleResumeEditing} disabled={busy}>
              Resume editing
            </Button>
          )}
          {chatPhase === 'finalized' && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={dataPlatformPushing ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              onClick={handleSendToDataPlatform}
              disabled={busy || dataPlatformPushing}
              title="Send the published, finalized SOA to the platform Data Platform Document Service."
            >
              {dataPlatformPushing ? 'Sending…' : 'Send to Data Platform'}
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ flexShrink: 0 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ flexShrink: 0 }}>
          {successMessage}
        </Alert>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '340px minmax(0, 1fr)' },
          gridTemplateRows: { xs: 'minmax(320px, 42vh) minmax(360px, 1fr)', md: '1fr' },
          gap: 1.5,
        }}
      >
        {/* Chat pane */}
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            borderRadius: 2,
          }}
        >
          <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              SOA Architect
            </Typography>
          </Box>

          <Box
            ref={chatScrollRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              px: 1.5,
              py: 1.5,
              bgcolor: 'grey.50',
            }}
          >
            {messages.length === 0 ? (
              <Box
                sx={{
                  height: '100%',
                  minHeight: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 2,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Start Section 1.3 to build columns and assessment rows before placing visit marks.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.25}>
                {messages.map((m) => {
                  const isUser = m.role === 'user';
                  return (
                    <Box
                      key={m.id}
                      sx={{
                        alignSelf: isUser ? 'flex-end' : 'flex-start',
                        maxWidth: '92%',
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: isUser ? 'primary.main' : 'background.paper',
                        color: isUser ? 'primary.contrastText' : 'text.primary',
                        border: isUser ? 'none' : '1px solid',
                        borderColor: 'divider',
                        boxShadow: isUser ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          opacity: isUser ? 0.85 : 1,
                          color: isUser ? 'inherit' : 'text.secondary',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          mb: 0.35,
                        }}
                      >
                        {m.role}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                        {m.rawContent}
                      </Typography>
                    </Box>
                  );
                })}
                {busy && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                      Updating schedule…
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}
          </Box>

          <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0, bgcolor: 'background.paper' }}>
            {messages.length === 0 && (
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={() => sendMessage(BOOTSTRAP_MESSAGE)}
                disabled={busy}
                fullWidth
                sx={{ mb: 1.25 }}
              >
                Start Section 1.3
              </Button>
            )}
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                size="small"
                placeholder="Describe visits, assessments, or mark changes…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(draft);
                  }
                }}
                disabled={busy}
              />
              <IconButton
                color="primary"
                onClick={() => sendMessage(draft)}
                disabled={busy || !draft.trim()}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  borderRadius: 1.5,
                  width: 40,
                  height: 40,
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                }}
              >
                {busy ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
              </IconButton>
            </Stack>
          </Box>
        </Paper>

        {/* SOA canvas + decisions */}
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Schedule of Activities
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {tables.length === 0 ? 'No tables yet' : `${tables.length} table${tables.length === 1 ? '' : 's'}`}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
            <Accordion
              defaultExpanded={false}
              disableGutters
              elevation={0}
              sx={{
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, minHeight: 44 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Expected decisions
                  </Typography>
                  <Chip size="small" label={`${decidedCount}/${decisions.length}`} variant="outlined" />
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      xl: 'repeat(3, minmax(0, 1fr))',
                    },
                    gap: 1.25,
                  }}
                >
                  {decisions.map((d) => (
                    <Box
                      key={d.label}
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                        minWidth: 0,
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                          {d.label}
                        </Typography>
                        <Chip size="small" label={d.status || 'pending'} color={decisionStatusColor(d.status)} />
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {d.value || '—'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            {tables.length === 0 ? (
              <Box
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: { xs: 3, md: 5 },
                  textAlign: 'center',
                  color: 'text.secondary',
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant="body2">
                  No SOA tables yet. Use &quot;Start Section 1.3&quot; in the chat pane.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={3} divider={<Divider flexItem />}>
                {tables.map((table) => (
                  <Box key={table.tableId || table.label} sx={{ minWidth: 0, overflowX: 'auto' }}>
                    <SoaTableRenderer
                      table={table}
                      allTables={tables}
                      variant="protocol"
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>

      <Dialog open={versionsOpen} onClose={() => setVersionsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>SOA version history</DialogTitle>
        <DialogContent dividers>
          {versions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No published versions yet. Use &quot;Publish to protocol&quot; after building tables.
            </Typography>
          ) : (
            <List dense>
              {versions.map((v) => (
                <ListItem key={v._id || v.versionNumber} divider>
                  <ListItemText
                    primary={`Version ${v.versionNumber}${v.isLatestVersion ? ' (latest)' : ''}`}
                    secondary={`${v.changeType || 'published'}${v.changeNote ? ` — ${v.changeNote}` : ''}${
                      v.createdAt ? ` · ${new Date(v.createdAt).toLocaleString()}` : ''
                    }`}
                  />
                  <ListItemSecondaryAction>
                    {!v.isLatestVersion && (
                      <Button
                        size="small"
                        onClick={() => handleRollback(v.versionNumber)}
                        disabled={busy}
                      >
                        Restore
                      </Button>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default S9AssessmentScheduleForm;
